// src/components/admin-tools/tools-api.ts
//
// POST /v1/admin/tools (criação de tool) — o api.ts não tem este método e está
// fora do escopo desta frente (não tocar). Segue o MESMO padrão do
// src/lib/api-pipelines.ts: base + token espelhados do api.ts.
//
// Contrato REAL do backend (raios src/admin/tools.routes.ts POST /admin/tools):
//   body aceito: { slug, name, scope_text } obrigatórios
//                + { category?, is_mutating?, requires_admin? } opcionais.
//   NÃO aceitos no POST: is_core, mcp_exposed, is_companion_only, is_active —
//   esses só via PATCH /v1/admin/tools/:slug DEPOIS do create (a row já existe
//   em pending_deploy). O create dispara create_tool (Mario gera handler +
//   migration, grava artifacts, INSERT pending_deploy + abre PR) de forma
//   SÍNCRONA — pode demorar minutos.

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TOKEN_STORAGE_KEY = 'raios_token'; // mesma chave do api.ts

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string };
}

export interface CreateToolInput {
  slug: string;
  name: string;
  /** Descrição/escopo textual da tool (vira tools.description + prompt do Mario). */
  scope_text: string;
  category?: 'dispatch' | 'query' | 'write' | 'external';
  is_mutating?: boolean;
  requires_admin?: boolean;
}

export interface CreateToolResult {
  slug: string;
  status: string; // sempre 'pending_deploy' no create
  is_active: boolean; // sempre false no create
  scope?: string;
  pr_number?: number;
  pr_url?: string;
  branch?: string;
  runner_module?: string;
  message?: string;
}

export async function createAdminTool(input: CreateToolInput): Promise<CreateToolResult> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}/v1/admin/tools`, {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });

  const rawText = await res.text();
  let json: ApiResponse<CreateToolResult>;
  try {
    json = rawText ? JSON.parse(rawText) : ({} as ApiResponse<CreateToolResult>);
  } catch {
    const preview = rawText.slice(0, 300).replace(/\s+/g, ' ');
    throw new Error(`Resposta inválida do servidor (HTTP ${res.status}): ${preview}`);
  }

  if (!res.ok || json.success === false) {
    throw new Error(json.error?.message ?? `Erro ${res.status} ao criar tool`);
  }
  return json.data;
}
